import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { DeliveryRoute } from '@restaurant/api-client';
import { env } from '@/env';

const TOKEN = env.mapboxToken;

type LngLat = [number, number];
type Point = { latitude: number; longitude: number };

function toLngLat([lng, lat]: number[]): LngLat[] {
  return lng === undefined || lat === undefined ? [] : [[lng, lat]];
}

/**
 * The delivery order's location on a map, in two views:
 *   - `route`       → the driving line the restaurant snapshotted at placement
 *                     (origin → door), fit to the whole route. Only selectable
 *                     when `route` is present; older orders have none.
 *   - `destination` → just the drop point, zoomed in for a precise "where".
 *
 * Read-only. The map instance is created once; toggling `mode` re-fits the view
 * and shows/hides the route line without a re-mount. Renders nothing when no
 * MapBox token is configured (same posture as the zone-preview map).
 */
export function OrderRouteMap({
  origin,
  destination,
  route,
  mode,
  height = 240,
}: {
  // Restaurant location — the route's start + a second marker. Null when the
  // restaurant hasn't pinned its location (then only the destination shows).
  origin: Point | null;
  destination: Point;
  route: DeliveryRoute | null;
  mode: 'route' | 'destination';
  height?: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const loadedRef = useRef(false);

  const dest: LngLat = [destination.longitude, destination.latitude];
  const orig: LngLat | null = origin ? [origin.longitude, origin.latitude] : null;

  // Re-fit the view for the current mode. Safe to call only once the style has
  // loaded (the route layer exists). Kept as a ref so both the load handler and
  // the mode effect call the SAME latest closure (fresh `mode`/`route`/`dest`).
  const applyViewRef = useRef<(map: mapboxgl.Map) => void>(() => {});
  applyViewRef.current = (map: mapboxgl.Map) => {
    const [first, ...rest] =
      mode === 'route' && route ? route.geometry.coordinates.flatMap(toLngLat) : [];
    if (first) {
      map.setLayoutProperty('route-line', 'visibility', 'visible');
      let bounds = new mapboxgl.LngLatBounds(first, first);
      for (const c of rest) bounds = bounds.extend(c);
      map.fitBounds(bounds, { padding: 44, duration: 300, maxZoom: 16 });
      return;
    }
    if (map.getLayer('route-line')) {
      map.setLayoutProperty('route-line', 'visibility', 'none');
    }
    map.easeTo({ center: dest, zoom: 15, duration: 300 });
  };

  // Create the map once. The parent keeps it mounted across mode toggles.
  useEffect(() => {
    if (!TOKEN || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: dest,
      zoom: 14,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    map.on('load', () => {
      // Drop point (red) + restaurant origin (dark), so both ends of the leg read
      // at a glance even in destination view.
      new mapboxgl.Marker({ color: '#ef4444' }).setLngLat(dest).addTo(map);
      if (orig) new mapboxgl.Marker({ color: '#1f2937' }).setLngLat(orig).addTo(map);

      if (route) {
        map.addSource('route', {
          type: 'geojson',
          data: { type: 'Feature', properties: {}, geometry: route.geometry },
        });
        map.addLayer({
          id: 'route-line',
          type: 'line',
          source: 'route',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#f97316', 'line-width': 4 },
        });
      }
      loadedRef.current = true;
      applyViewRef.current(map);
    });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      loadedRef.current = false;
    };
    // Init once — origin/destination/route don't change for a placed order.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fit when the operator flips the toggle.
  useEffect(() => {
    const map = mapRef.current;
    if (map && loadedRef.current) applyViewRef.current(map);
  }, [mode]);

  if (!TOKEN) return null;
  return <div ref={containerRef} style={{ width: '100%', height, borderRadius: 12 }} />;
}
